import { CUSTOM_VALUE } from '@src/components/MarketSelector/tokens';
import {
  AggregationIcon,
  BaseSelect,
  EMPTY_ARRAY,
  FormControlSizes,
  HStack,
  type Security,
  useCallbackRef,
  useMarketAccountsContext,
  useMarketsContext,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useMarketSelectorBuyingPowerEnrichment } from '../../../../components/MarketSelector/enrichments/useMarketSelectorBuyingPowerEnrichment';
import { useUnsettledEnrichment } from '../../../../components/MarketSelector/enrichments/useUnsettledEnrichment';
import { useMarketDataMarketSelectorItems } from '../../../../components/MarketSelector/MarketData/useMarketDataMarketSelectorItems';
import {
  MarketSelectorContextProvider,
  useMarketSelectorContext,
} from '../../../../components/MarketSelector/MarketSelectorContext';
import {
  MarketSelectorPanel,
  type MarketSelectorPanelProps,
} from '../../../../components/MarketSelector/MarketSelectorPanel';
import { useMarketSelectorAggregationPresets } from '../../../../components/MarketSelector/presets/useMarketSelectorAggregationPresets';
import type { MarketSelectorValue, UseMarketSelectorPanelParams } from '../../../../components/MarketSelector/types';
import { useMarketSelector } from '../../../../components/MarketSelector/useMarketSelector';
import { useMarketSelectorPanel } from '../../../../components/MarketSelector/useMarketSelectorPanel';
import { useDeepDiveContext } from '../../../../providers/DeepDiveProvider';

interface DeepDiveMarketSelectorProps {
  security: Security;
  isNarrow?: boolean;
}

function DeepDiveMarketSelectorInner() {
  const { presetsByID, preset, selections } = useMarketSelectorContext();

  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const handleSelectClick = useCallback(() => {
    setIsOpen(open => !open);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const getLabel = useCallback(
    (value: string) => {
      if (value === CUSTOM_VALUE) {
        return 'Custom';
      }

      return presetsByID?.get(value)?.label ?? value;
    },
    [presetsByID]
  );

  const selectorValue = selections.length === 0 ? undefined : preset ? preset : CUSTOM_VALUE;

  return (
    <HStack ref={panelRef} gap="spacing4" minWidth="200px" w="200px" data-testid="market-selector">
      <BaseSelect
        value={selectorValue}
        getLabel={getLabel}
        prefix={<AggregationIcon />}
        isDropdownOpened={isOpen}
        onClick={handleSelectClick}
        placeholder="Select..."
        size={FormControlSizes.Small}
        dataTestID="market-selector-main-select-button"
      />
      <MarketSelectorPanelStandalone
        isOpen={isOpen}
        close={handleClose}
        panelOffset={0}
        panelAnchorRef={panelRef}
        panelPlacement="bottom-start"
        testID="market-selector-panel"
      />
    </HStack>
  );
}

export function DeepDiveMarketSelector({ security, isNarrow = false }: DeepDiveMarketSelectorProps): JSX.Element {
  const marketSelectorItems = useMarketDataMarketSelectorItems({
    symbol: security?.Symbol,
  });

  const unsettledEnrichment = useUnsettledEnrichment({ security, marketSelectorItems });

  const buyingPowerEnrichment = useMarketSelectorBuyingPowerEnrichment({
    symbol: security?.Symbol ?? '',
    currency: security?.BaseCurrency ?? '',
    threshold: '10s',
    quantity: undefined,
    tag: 'DeepDiveMarketSelector',
    side: undefined,
    simpleModeVisibleColIDs: EMPTY_ARRAY,
  });

  const enrichmentSpecs = useMemo(
    () => compact([unsettledEnrichment, buyingPowerEnrichment]),
    [unsettledEnrichment, buyingPowerEnrichment]
  );

  const { marketsByName } = useMarketsContext();
  const { marketAccountsByName } = useMarketAccountsContext();
  const {
    selectedCombined,
    setSelectedMarketAccounts,
    setSelectedMarkets,
    selectedAggregation,
    setSelectedAggregation,
  } = useDeepDiveContext();

  const handleNewMarketSelectorChange = useCallbackRef((value: MarketSelectorValue) => {
    const selections = value.selections;
    const markets = selections.filter(mktOrAccName => marketsByName.has(mktOrAccName));
    const marketAccounts = selections.filter(mktOrAccName => marketAccountsByName.has(mktOrAccName));
    setSelectedMarkets(markets);
    setSelectedMarketAccounts(marketAccounts);
    setSelectedAggregation(value.type === 'preset' ? value.presetID : undefined);
  });

  const newMarketSelectorValue: MarketSelectorValue = useMemo(() => {
    return selectedAggregation
      ? { type: 'preset', presetID: selectedAggregation, selections: selectedCombined }
      : { type: 'custom', selections: selectedCombined };
  }, [selectedCombined, selectedAggregation]);

  const { aggregationPresets } = useMarketSelectorAggregationPresets();

  const marketSelectorAPI = useMarketSelector({
    value: newMarketSelectorValue,
    onChange: handleNewMarketSelectorChange,
    items: marketSelectorItems,
    presets: aggregationPresets,
    enrichmentSpecs,
  });

  return (
    <MarketSelectorContextProvider marketSelectorAPI={marketSelectorAPI} isNarrow={isNarrow} showCredentialsOnlyToggle>
      <DeepDiveMarketSelectorInner />
    </MarketSelectorContextProvider>
  );
}

const MarketSelectorPanelStandalone = ({
  isOpen,
  close,
  panelAnchorRef,
  panelOffset,
  panelPlacement,
  testID,
}: UseMarketSelectorPanelParams & Pick<MarketSelectorPanelProps, 'testID'>) => {
  const dropdownProps = useMarketSelectorPanel({
    isOpen,
    close,
    panelAnchorRef,
    panelOffset,
    panelPlacement,
  });
  return <MarketSelectorPanel dropdownProps={dropdownProps} testID={testID} />;
};
